import { deleteData, getData, postData, putData } from "../api/api";
import type { PageResponse } from "../types/dashboard";

export interface AdaptorDiscoveryDraft {
  id?: string;
  adaptorId?: string;
  adaptorName?: string;
  objectNames?: string[];
  draftTitle?: string;
  draftMarkdown?: string;
  schemaHash?: string;
  fieldCount?: number;
  status?: "DRAFT" | "APPROVED" | "REJECTED" | "INGESTING" | "INGESTED";
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  guideDocumentId?: string;
  analysisText?: string;
  analysisApprovedBy?: string;
  analysisApprovedAt?: string;
  createdDate?: string;
}

export interface AdaptorDiscoveryResponse {
  draftId?: string;
  adaptorId?: string;
  adaptorName?: string;
  objectNames?: string[];
  schemaHash?: string;
  fieldCount?: number;
  draftTitle?: string;
  draftMarkdown?: string;
}

export const adaptorDiscoveryService = {
  discover(adaptorId: string, objectNames: string[]) {
    return postData("/admin/adaptor-discovery/discover", {
      adaptorId,
      objectNames,
    }) as Promise<AdaptorDiscoveryResponse>;
  },
  listDrafts(page = 0, size = 20) {
    return getData(
      `/admin/adaptor-discovery/drafts?page=${page}&size=${size}`
    ) as Promise<PageResponse<AdaptorDiscoveryDraft>>;
  },
  getDraft(id: string) {
    return getData(`/admin/adaptor-discovery/drafts/${id}`) as Promise<AdaptorDiscoveryDraft>;
  },
  getAnalysis(id: string) {
    return getData(`/admin/adaptor-discovery/drafts/${id}/analysis`) as Promise<{
      analysisText: string;
      adaptorName: string;
      draftTitle: string;
      status: string;
    }>;
  },
  transferToRag(id: string) {
    return postData(
      `/admin/adaptor-discovery/drafts/${id}/transfer-to-rag`,
      {}
    ) as Promise<AdaptorDiscoveryDraft>;
  },
  reanalyze(id: string) {
    return postData(
      `/admin/adaptor-discovery/drafts/${id}/reanalyze`,
      {}
    ) as Promise<AdaptorDiscoveryDraft>;
  },
  reingest(id: string) {
    return postData(
      `/admin/adaptor-discovery/drafts/${id}/reingest`,
      {}
    ) as Promise<AdaptorDiscoveryDraft>;
  },
  /** Permanently delete draft; removes RAG guide/chunks when present. */
  remove(id: string) {
    return deleteData(`/admin/adaptor-discovery/drafts/${id}`) as Promise<void>;
  },
  deleteIngested(id: string) {
    return deleteData(
      `/admin/adaptor-discovery/drafts/${id}/ingested`
    ) as Promise<AdaptorDiscoveryDraft>;
  },
  deleteRejected(id: string) {
    return deleteData(`/admin/adaptor-discovery/drafts/${id}/rejected`) as Promise<void>;
  },
  generateAnalysis(id: string) {
    return postData(`/admin/adaptor-discovery/drafts/${id}/analysis/generate`, {}) as Promise<{
      analysisText: string;
    }>;
  },
  updateAnalysis(id: string, analysisText: string) {
    return putData(`/admin/adaptor-discovery/drafts/${id}/analysis`, {
      analysisText,
    }) as Promise<{ analysisText: string }>;
  },
  approveAnalysis(id: string, analysisText?: string) {
    return postData(
      `/admin/adaptor-discovery/drafts/${id}/analysis/approve`,
      analysisText ? { analysisText } : {}
    ) as Promise<AdaptorDiscoveryDraft>;
  },
  reject(id: string, reason?: string) {
    return postData(
      `/admin/adaptor-discovery/drafts/${id}/reject`,
      reason ? { reason } : {}
    ) as Promise<AdaptorDiscoveryDraft>;
  },
  checkChanges(autoCreateDraft = false) {
    return postData(
      `/admin/adaptor-discovery/check-changes?autoCreateDraft=${autoCreateDraft}`,
      {}
    ) as Promise<
      Array<{
        adaptorId: string;
        adaptorName?: string;
        storedSchemaHash?: string;
        currentSchemaHash?: string;
        latestDraftId?: string;
        draftCreated?: boolean;
      }>
    >;
  },
};
